import { useCallback, useEffect, useState } from 'react';
import { catalogService, TriggerEntity, TriggerEvent } from '../services/catalogService';

export interface CatalogState {
  entities: TriggerEntity[];
  events: TriggerEvent[];
  isLoading: boolean;
}

export const useCatalog = () => {
  const [state, setState] = useState<CatalogState>({
    entities: [],
    events: [],
    isLoading: false,
  });

  const fetchCatalog = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const data = await catalogService.getEntities();
      const entities = Array.isArray(data) ? data : [];

      setState(prev => ({
        ...prev,
        entities,
        isLoading: false,
      }));

      // Fetch events for each entity to have a full mapping
      const eventResults = await Promise.all(entities.map(entity => catalogService.getEvents(entity.entityType)));

      const allEvents: TriggerEvent[] = [];
      eventResults.flat().forEach(newEvent => {
        if (!allEvents.find(e => e.eventId === newEvent.eventId)) {
          allEvents.push(newEvent);
        }
      });

      setState(prev => ({
        ...prev,
        events: allEvents,
      }));
    } catch (error) {
      console.error('Failed to fetch catalog', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const getEntityName = useCallback(
    (type: string): string => {
      const entity = state.entities.find(e => e.entityType === type);
      return entity?.entityName || type;
    },
    [state.entities]
  );

  const getEventName = useCallback(
    (id: string): string => {
      const event = state.events.find(e => e.eventId === id);
      return event?.eventName || id;
    },
    [state.events]
  );

  return {
    ...state,
    fetchCatalog,
    getEntityName,
    getEventName,
  };
};
